import React, { useState, useEffect } from 'react';
import { ListOrdered, Plus, Trash2, Sparkles } from 'lucide-react';
import ActivityLayout, {
    Toggle, SettingsGroup, SettingsRow, SubRow,
    InlineInput, AIButton, AIUndoButton, AIOptionButton, PollResults, AddImageSection, QuestionImage,
} from './ActivityLayout';
import Tooltip from '../../components/ui/Tooltip';
import styles from './ActivityLayout.module.css';
import useActivityContent from '../../hooks/useActivityContent';
import ParticipantExperience from '../join/ParticipantExperience';
import { API_PATH, request } from '../../config/api';
import { useConfirm } from '../../store/ConfirmDialogContext';

const DUMMY_DATA = {
    question: 'Rank these factors from most to least important when choosing a job 💼',
    options: ['Salary & Benefits', 'Work-Life Balance', 'Company Culture', 'Career Growth', 'Remote Work'],
    settings: { restrictedRanking: false, requiredOptions: 3, randomize: true, description: true, descText: 'Drag to reorder — put what matters most to you at the top.', pollResults: 'hidden', isLocked: false, allowUpdateResponse: true, imageUrl: null },
};

const Ranking = ({
    event, activity, isLaunched, onLaunch, onStop,
    onNext, onPrev, hasNext, hasPrev, onSettingsUpdated
}) => {
    const confirm = useConfirm();
    const [question, setQuestion] = useState('');
    const [options, setOptions] = useState(['Option A', 'Option B', 'Option C']);
    const [editingQuestion, setEditingQuestion] = useState(false);
    const [editingDesc, setEditingDesc] = useState(false);
    const [settings, setSettings] = useState({
        restrictedRanking: false,
        requiredOptions: 3,
        randomize: false,
        description: false,
        descText: '',
        pollResults: 'hidden',
        isLocked: false,
        allowUpdateResponse: true,
        imageUrl: null,
    });

    const { content, isLoading, isSaving, saveStatus, saveContent } =
        useActivityContent(event?.id, activity?.id);

    const [isImproving, setIsImproving] = useState(false);
    const [isImprovingDesc, setIsImprovingDesc] = useState(false);
    const [isAddingOptions, setIsAddingOptions] = useState(false);
    const [previousQuestion, setPreviousQuestion] = useState(null);
    const [previousDescText, setPreviousDescText] = useState(null);
    const API_BASE = API_PATH;

    const handleImproveWithAI = async () => {
        if (!question.trim()) return;
        setIsImproving(true);
        try {
            const res = await request(`${API_BASE}/ai/suggest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'improve', text: question }),
            });
            if (res.ok) {
                const data = await res.json();
                if (data.suggestion) {
                    setPreviousQuestion(question);
                    setQuestion(data.suggestion);
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsImproving(false);
        }
    };

    const handleUndoQuestion = () => {
        if (previousQuestion !== null) {
            setQuestion(previousQuestion);
            setPreviousQuestion(null);
        }
    };

    const handleImproveDescWithAI = async () => {
        const text = settings.descText?.trim() || '';
        if (!text) return;
        setIsImprovingDesc(true);
        try {
            const res = await request(`${API_BASE}/ai/suggest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'improve', text }),
            });
            if (res.ok) {
                const data = await res.json();
                if (data.suggestion) {
                    setPreviousDescText(text);
                    set('descText', data.suggestion);
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsImprovingDesc(false);
        }
    };

    const handleUndoDesc = () => {
        if (previousDescText !== null) {
            set('descText', previousDescText);
            setPreviousDescText(null);
        }
    };

    const handleAddOptionsWithAI = async () => {
        setIsAddingOptions(true);
        try {
            const res = await request(`${API_BASE}/ai/suggest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'options', context: { question } }),
            });
            if (res.ok) {
                const data = await res.json();
                if (data.suggestions) {
                    setOptions(prev => [...prev, ...data.suggestions]);
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsAddingOptions(false);
        }
    };

    useEffect(() => {
        if (!content || Object.keys(content).length === 0) return;
        if (content.question !== undefined) setQuestion(content.question);
        if (content.options) setOptions(content.options);
        if (content.settings) setSettings(s => ({ ...s, ...content.settings }));
    }, [content]);

    const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
    const openQuestionEditor = () => setEditingQuestion(true);
    const closeQuestionEditor = () => setEditingQuestion(false);
    const openDescEditor = () => setEditingDesc(true);
    const closeDescEditor = () => setEditingDesc(false);
    const handleKeyToEdit = (e, openFn) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFn(); }
    };
    const handleEditKeyDown = (e, closeFn) => {
        if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); closeFn(); }
    };
    const addOption = () => setOptions([...options, '']);
    const removeOption = async (i) => {
        const responseCount = activity?.response_count || 0;
        const ok = await confirm({
            title: 'Delete Option',
            message: 'Delete this option?',
            details: responseCount > 0 ? (
                <>
                    <span style={{ fontWeight: 600 }}>⚠️ This poll already has {responseCount} response{responseCount !== 1 ? 's' : ''}.</span>
                    <br />
                    Existing rankings that included this item will no longer show it in results.
                </>
            ) : undefined,
            confirmText: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        setOptions(options.filter((_, j) => j !== i));
    };
    const updateOption = (i, v) => setOptions(options.map((o, j) => (j === i ? v : o)));

    const right = (
        <>
            <SettingsGroup title="Poll Settings">
                {/* Restricted Ranking */}
                <SettingsRow label="Restricted ranking" description="Set required number of options to rank">
                    <Toggle checked={settings.restrictedRanking} onChange={(e) => set('restrictedRanking', e.target.checked)} />
                </SettingsRow>
                {settings.restrictedRanking && (
                    <SubRow>
                        <span>Required options:</span>
                        <InlineInput
                            type="number"
                            value={settings.requiredOptions}
                            onChange={(e) => set('requiredOptions', e.target.value)}
                            min={1}
                            max={options.length}
                        />
                    </SubRow>
                )}

                {/* Randomize Options */}
                <SettingsRow label="Randomize options" description="Randomly reorder poll options for each participant">
                    <Toggle checked={settings.randomize} onChange={(e) => set('randomize', e.target.checked)} />
                </SettingsRow>

                <SettingsRow label="Poll description" description="Add description visible to participants">
                    <Toggle checked={settings.description} onChange={(e) => set('description', e.target.checked)} />
                </SettingsRow>
            </SettingsGroup>

            <SettingsGroup title="Poll Results">
                <PollResults value={settings.pollResults} onChange={(v) => set('pollResults', v)} />
                <SettingsRow label="Allow participants to update response" description="Let participants change their answer after submitting">
                    <Toggle checked={settings.allowUpdateResponse !== false} onChange={(e) => set('allowUpdateResponse', e.target.checked)} />
                </SettingsRow>
            </SettingsGroup>

            <SettingsGroup>
                <AddImageSection
                    imageUrl={settings.imageUrl}
                    onImageChange={(url) => set('imageUrl', url)}
                />
            </SettingsGroup>
        </>
    );

    const left = (
        <>
            <div className={`${styles.questionCard} ${settings.imageUrl ? styles.questionCardWithImage : ''}`}>
                {settings.imageUrl && (
                    <QuestionImage imageUrl={settings.imageUrl} onImageChange={(url) => set('imageUrl', url)} />
                )}
                <div className={styles.questionContent}>
                    <div className={styles.questionLabelRow}>
                        <span className={styles.questionLabel}>Question <span className={styles.requiredStar}>*</span></span>
                        <span className={styles.aiActions}>
                            <AIButton onClick={handleImproveWithAI} loading={isImproving} />
                            {previousQuestion !== null && (
                                <AIUndoButton onClick={handleUndoQuestion} label="Undo" />
                            )}
                        </span>
                    </div>
                    {editingQuestion ? (
                        <input
                            className={styles.questionInput}
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            placeholder="Rank the following items..."
                            autoFocus
                            onBlur={closeQuestionEditor}
                            onKeyDown={(e) => handleEditKeyDown(e, closeQuestionEditor)}
                        />
                    ) : (
                        <Tooltip title="Click to edit question" placement="top">
                            <div
                                className={`${styles.questionTextDisplay} ${!question ? styles.questionTextPlaceholder : ''}`}
                                role="button"
                                tabIndex={0}
                                onClick={openQuestionEditor}
                                onKeyDown={(e) => handleKeyToEdit(e, openQuestionEditor)}
                            >
                                {question || 'Type your question here...'}
                            </div>
                        </Tooltip>
                    )}

                    {settings.description && (
                        <div className={styles.questionDescription}>
                            <div className={styles.questionLabelRow}>
                                <span className={styles.descLabel}>Description</span>
                                <span className={styles.aiActions}>
                                    <AIButton onClick={handleImproveDescWithAI} loading={isImprovingDesc} />
                                    {previousDescText !== null && (
                                        <AIUndoButton onClick={handleUndoDesc} label="Undo" />
                                    )}
                                </span>
                            </div>
                            {editingDesc ? (
                                <input
                                    className={styles.descInput}
                                    value={settings.descText}
                                    onChange={(e) => set('descText', e.target.value)}
                                    placeholder="Type description here..."
                                    autoFocus
                                    onBlur={closeDescEditor}
                                    onKeyDown={(e) => handleEditKeyDown(e, closeDescEditor)}
                                />
                            ) : (
                                <Tooltip title="Click to edit description" placement="top">
                                    <div
                                        className={`${styles.descTextDisplay} ${!settings.descText ? styles.descTextPlaceholder : ''}`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={openDescEditor}
                                        onKeyDown={(e) => handleKeyToEdit(e, openDescEditor)}
                                    >
                                        {settings.descText || 'Type description here...'}
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.optionsCard}>
                <div className={styles.optionsLabel}>Options to rank</div>
                <p className={styles.optionsHint}>
                    <strong>How it works:</strong> Participants see these options and <strong>drag them</strong> to set their order — <strong>#1 = top choice</strong>. They tap Submit when done. Results show which option was ranked #1 most often and the full ranking breakdown.
                </p>
                {options.map((opt, i) => (
                    <div key={i} className={styles.optionRow}>
                        <span className={styles.optionNumber}>{i + 1}</span>
                        <input
                            className={styles.optionInput}
                            value={opt}
                            onChange={(e) => updateOption(i, e.target.value)}
                            placeholder={`Option ${i + 1}`}
                        />
                        {options.length > 2 && (
                            <button className={styles.optionRemoveBtn} onClick={() => removeOption(i)} aria-label="Remove option">
                                <Trash2 size={14} />
                            </button>
                        )}
                    </div>
                ))}
                <div className={styles.optionsFooter}>
                    <button className={styles.addOptionBtn} onClick={addOption}>
                        <Plus size={14} /> Add option
                    </button>
                    <AIOptionButton onClick={handleAddOptionsWithAI} loading={isAddingOptions} />
                </div>
            </div>
        </>
    );

    const loadDummy = () => {
        setQuestion(DUMMY_DATA.question);
        setOptions(DUMMY_DATA.options);
        setSettings(s => ({ ...s, ...DUMMY_DATA.settings }));
    };

    const participantPreview = (
        <ParticipantExperience
            step="activity"
            name="Preview User"
            event={event}
            activity={{
                type: 'ranking',
                title: question || 'Ranking',
                content: { question, options, settings }
            }}
        />
    );

    return (
        <ActivityLayout
            icon={<ListOrdered size={20} />}
            color="var(--cat-orange)"
            title="Ranking"
            eventName={event?.name}
            event={event}
            activity={activity}
            left={left}
            right={right}
            participantPreview={participantPreview}
            validate={() => !question.trim() ? 'Question is required.' : null}
            onSave={() => {
                setPreviousQuestion(null);
                setPreviousDescText(null);
                saveContent({ question, options, settings });
            }}
            onLoadExample={loadDummy}
            isSaving={isSaving}
            saveStatus={saveStatus}
            isLoading={isLoading}
            isLaunched={isLaunched}
            onLaunch={onLaunch}
            onStop={onStop}
            onNext={onNext}
            onPrev={onPrev}
            hasNext={hasNext}
            hasPrev={hasPrev}
            onSettingsUpdated={onSettingsUpdated}
        />
    );
};

export default Ranking;
